import { Moon, Sun, Monitor, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/hooks/useTheme';
import { Theme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';

interface User {
  first_name: string;
  last_name: string;
  display_name?: string;
  roles: Array<{ name: string; display_name: string }>;
}

interface UserProfileMenuProps {
  user: User | null;
  collapsed?: boolean;
}

const themeOptions: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

export function UserProfileMenu({
  user,
  collapsed = false,
}: UserProfileMenuProps) {
  const { theme, setTheme } = useTheme();

  const getUserInitials = () => {
    if (!user) return 'U';
    return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
  };

  const getUserRole = () => {
    if (!user || !user.roles.length) return 'User';
    const sortedRoles = [...user.roles].sort((a, b) => {
      const priority: Record<string, number> = {
        super_admin: 4,
        general_partner: 3,
        fund_accountant: 2,
        compliance_officer: 1,
        analyst: 0,
      };
      return (priority[b.name] || 0) - (priority[a.name] || 0);
    });
    return sortedRoles[0].display_name;
  };

  const currentThemeIcon =
    themeOptions.find((t) => t.value === theme)?.icon || Monitor;
  const ThemeIcon = currentThemeIcon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex items-center w-full rounded-md transition-colors hover:bg-accent p-2',
            collapsed ? 'justify-center' : 'gap-3'
          )}
        >
          <div className="h-8 w-8 shrink-0 rounded-full bg-primary flex items-center justify-center">
            <span className="text-sm font-medium text-primary-foreground">
              {getUserInitials()}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground truncate">
                {user?.display_name || `${user?.first_name} ${user?.last_name}`}
              </p>
              <p className="text-xs text-muted-foreground">{getUserRole()}</p>
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={collapsed ? 'right' : 'top'}
        align={collapsed ? 'center' : 'start'}
        className="w-56"
      >
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">
              {user?.display_name || `${user?.first_name} ${user?.last_name}`}
            </p>
            <p className="text-xs text-muted-foreground">{getUserRole()}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <ThemeIcon className="mr-2 h-4 w-4" />
            <span>Theme</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {themeOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = theme === option.value;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => setTheme(option.value)}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  <span>{option.label}</span>
                  {isSelected && <Check className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
